import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export const FeaturePageLayout = ({
  icon,
  title,
  badge,
  description,
  capabilities,
  howItWorks,
  useCases,
  ctaText,
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <PageHeader
        icon={icon}
        title={title}
        badge={badge}
        description={description}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Capabilities */}
        {capabilities && capabilities.length > 0 && (
          <motion.section
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="font-heading text-xl lg:text-2xl font-semibold text-foreground mb-6">
              Key Capabilities
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {capabilities.map((cap, i) => (
                <motion.div
                  key={cap.title}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.5 + i * 0.07 }}
                  className="glass-card rounded-xl p-5 flex gap-3"
                >
                  <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground font-heading mb-1">
                      {cap.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      {cap.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* How It Works */}
        {howItWorks && howItWorks.length > 0 && (
          <motion.section
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="font-heading text-xl lg:text-2xl font-semibold text-foreground mb-6">
              How It Works
            </h2>
            <div className="space-y-4">
              {howItWorks.map((step, i) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: -15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 + i * 0.1 }}
                  className="glass-card rounded-xl p-5 flex gap-4 items-start"
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-primary font-heading">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground font-heading mb-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Use Cases */}
        {useCases && useCases.length > 0 && (
          <motion.section
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mb-16"
          >
            <h2 className="font-heading text-xl lg:text-2xl font-semibold text-foreground mb-6">
              Use Cases
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {useCases.map((uc, i) => (
                <motion.div
                  key={uc.title}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.7 + i * 0.07 }}
                  className="glass-card rounded-xl p-5"
                >
                  <div className="text-2xl mb-3">{uc.emoji}</div>
                  <h3 className="text-sm font-semibold text-foreground font-heading mb-1">
                    {uc.title}
                  </h3>
                  <p className="text-xs text-muted-foreground font-body leading-relaxed">
                    {uc.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* CTA */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="glass-card rounded-2xl p-8 lg:p-10 text-center glow-border-primary"
        >
          <h2 className="font-heading text-xl lg:text-2xl font-bold text-foreground mb-3">
            {ctaText || 'Ready to get started?'}
          </h2>
          <p className="text-sm text-muted-foreground font-body mb-6">
            Try Agentick free for 7 days. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="premium" size="lg" asChild>
              <a href="https://app.agentick.in" target="_blank" rel="noopener noreferrer">
                Start Free Trial <ArrowRight size={16} />
              </a>
            </Button>
            <Button variant="glass" size="lg" asChild>
              <a href="https://app.agentick.in" target="_blank" rel="noopener noreferrer">Book a Demo</a>
            </Button>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};
